import { InfoCircleFilled } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { Alert, Form, InputNumber } from 'antd';
import { useEffect, useState } from 'react';

import { Mode } from 'app/components/commons/Hotel/Types';
import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { MultipleDatePicker } from 'app/components/pages/Collections/CollectionDetail/CollectionSections/MultipleDatePicker/MultipleDatePicker';

import './Discount.scss';

type DiscountFormValues = {
  minimumDiscountOverride: number | undefined;
  minimumDiscountOverrideDates: Date[];
};

type DiscountProps = {
  form: FormInstance<DiscountFormValues>;
  mode: Mode;
  onDirty?: () => void;
};

export const Discount = ({ form, mode, onDirty }: DiscountProps) => {
  const isDisabled = mode === Mode.View;

  const [showDiscount, setShowDiscount] = useState<boolean>(false);

  useEffect(() => {
    const discountValue = form.getFieldValue('minimumDiscountOverride');
    const discountDates = form.getFieldValue('minimumDiscountOverrideDates');

    setShowDiscount(
      discountValue !== undefined || (discountDates && discountDates.length > 0)
    );
  }, [form]);

  const handleSwitchChange = (checked: boolean) => {
    setShowDiscount(checked);
    onDirty && onDirty();

    if (!checked) {
      form.setFieldValue('minimumDiscountOverride', undefined);
      form.setFieldValue('minimumDiscountOverrideDates', []);
    }
  };

  return (
    <div className="switch-container">
      <div className="discount-switch-wrapper">
        <SwitchableInput
          name="minimumDiscount"
          title="Custom Minimum Discount"
          value={showDiscount}
          disabled={isDisabled}
          onChange={handleSwitchChange}
          noBackground
          noBorder
          childNode={
            showDiscount && (
              <div className="input-value">
                <div className="custom-form-item">
                  <label className="custom-label">Discount</label>
                  <div className="input-wrapper">
                    <Form.Item
                      name="minimumDiscountOverride"
                      rules={[
                        {
                          required: showDiscount,
                          message: 'Enter a minimum discount',
                        },
                        {
                          type: 'number',
                          min: 0,
                          max: 18,
                          message: 'Min. discount must be between 0% and 18%',
                        },
                      ]}
                      className="custom-form-item-ant"
                    >
                      <InputNumber
                        min={0}
                        addonAfter="%"
                        disabled={isDisabled}
                        className="discount-input"
                      />
                    </Form.Item>
                  </div>
                </div>

                <div className="custom-form-item">
                  <label className="custom-label">Dates</label>
                  <div className="input-wrapper date-picker">
                    <Form.Item
                      name="minimumDiscountOverrideDates"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!showDiscount) {
                              return Promise.resolve();
                            }

                            return value && value.length > 0
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error('Please select at least one date')
                                );
                          },
                        },
                      ]}
                      className="custom-form-item-ant"
                    >
                      <MultipleDatePicker disabled={isDisabled} />
                    </Form.Item>
                  </div>
                </div>

                <Alert
                  className="blue-variant"
                  type="info"
                  description={
                    <>
                      Please note that hotels will be{' '}
                      <strong>notified about the activation</strong> of this
                      custom minimum discount on their inventory.
                    </>
                  }
                  icon={<InfoCircleFilled />}
                  showIcon
                />
              </div>
            )
          }
        />
      </div>
    </div>
  );
};
